import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Button, Space, Alert, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createInventoryTransaction, type CreateInventoryTransactionDto } from '@/api/productApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const TYPE_OPTIONS = ['in', 'out', 'adjustment'];
const REFERENCE_TYPE_OPTIONS = ['order', 'manual', 'return'];

interface TxnFormValues {
  product_id: string;
  type: string;
  quantity: number;
  reference_type: string;
  reference_id?: string;
  notes?: string;
  warehouse_id?: string;
}

export default function InventoryTransactionForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<TxnFormValues>();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('products', 'write');

  const prefilledProductId = searchParams.get('product_id') || '';

  if (!canWrite) return <Alert type="warning" message={t('app.forbidden')} />;

  const handleSubmit = async (values: TxnFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const data: CreateInventoryTransactionDto = {
        product_id: values.product_id,
        type: values.type,
        quantity: values.quantity,
        reference_type: values.reference_type,
        reference_id: values.reference_id,
        notes: values.notes,
        warehouse_id: values.warehouse_id,
      };
      await createInventoryTransaction(data);
      message.success(t('products.create_success'));
      if (prefilledProductId) {
        navigate(`/products/${prefilledProductId}`);
      } else {
        navigate('/products');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('app.error');
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2>{t('inventory.new_transaction')}</h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form<TxnFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        scrollToFirstError
        style={{ maxWidth: 600 }}
        initialValues={{
          product_id: prefilledProductId,
          type: 'in',
          reference_type: 'manual',
          quantity: 1,
        }}
      >
        <Form.Item
          label={t('inventory.field.product_id')}
          name="product_id"
          rules={[{ required: true }]}
        >
          <Input placeholder={t('inventory.field.product_id')} />
        </Form.Item>

        <Form.Item
          label={t('inventory.field.type')}
          name="type"
          rules={[{ required: true }]}
        >
          <Select
            options={TYPE_OPTIONS.map((v) => ({
              value: v,
              label: t(`inventory.type.${v}`),
            }))}
          />
        </Form.Item>

        <Form.Item
          label={t('inventory.field.quantity')}
          name="quantity"
          rules={[{ required: true }]}
        >
          <InputNumber min={1} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          label={t('inventory.field.reference_type')}
          name="reference_type"
          rules={[{ required: true }]}
        >
          <Select
            options={REFERENCE_TYPE_OPTIONS.map((v) => ({
              value: v,
              label: t(`inventory.reference_type.${v}`),
            }))}
          />
        </Form.Item>

        <Form.Item label={t('inventory.field.reference_id')} name="reference_id">
          <Input />
        </Form.Item>

        <Form.Item label={t('inventory.field.warehouse_id')} name="warehouse_id">
          <Input />
        </Form.Item>

        <Form.Item label={t('inventory.field.notes')} name="notes">
          <TextArea rows={3} maxLength={2000} />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button onClick={() => navigate(-1)}>
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
